import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { fetchInternalServices, registerNewUser } from '@jobandtalent/auth-api-client-v2';
import {
  FetchInternalServices200,
  FetchInternalServicesParams,
  RegisterNewUser200,
  RegisterNewUserBodyUserServiceUserInfoAccountType
} from '@jobandtalent/auth-api-client-v2/dist/apiClient/model';
import { Checkbox, ErrorMessage, InputText, Select } from '@jobandtalent/design-system';

import useSWRInfinite from 'swr/infinite';

import FormInput from 'src/components/FormInput/FormInput';
import Section from 'src/components/Section/Section';
import StackView, { SaveCancelFooter } from 'src/components/StackView/StackView';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';

import { useUserState } from '../UserState';
import useUsersRouter from '../useUsersRouter';

type NewUserFormData = {
  name: string;
  email: string;
  time_zone: string;
  locale: string;
  phone_number: string;
  country_code: string;
  service_user: boolean;
  service_user_info: {
    owner: string;
    description: string;
    account_type: string;
    var_path: string;
    caller_internal_service_id: number | null;
    provider_internal_service_id: number | null;
  };
};

const getInternalServicesKey = (
  pageIndex: number,
  previousPageData: FetchInternalServices200
): FetchInternalServicesParams | null => {
  if (previousPageData && previousPageData.meta.next_page == null) {
    return null;
  }

  return { page: pageIndex + 1, per_page: 100 };
};

const normalizeInternalServiceNameForEmail = (name: string) =>
  name.trim().replace(/\s+/g, '-').toLowerCase();

const NewUserForm = () => {
  const { resetUserSelection } = useUserState();
  const { updateQuery } = useUsersRouter();
  const { permissions, error: permissionError } = useLocalPermissions();
  const [serviceUserFlag, setServiceUserFlag] = useState(false);
  const [accountType, setAccountType] =
    useState<RegisterNewUserBodyUserServiceUserInfoAccountType>();
  const isS2sUser = serviceUserFlag && accountType === 'service_to_service';

  const {
    data: internalServicesData,
    error: internalServicesError,
    size: internalServicesPages,
    setSize: setInternalServicesPages
  } = useSWRInfinite<FetchInternalServices200>(
    isS2sUser ? getInternalServicesKey : () => null,
    fetchInternalServices,
    { revalidateFirstPage: false }
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<NewUserFormData>({
    defaultValues: {
      name: '',
      email: '',
      time_zone: '',
      locale: '',
      phone_number: '',
      country_code: '',
      service_user: false,
      service_user_info: {
        owner: '',
        description: '',
        account_type: '',
        var_path: '',
        caller_internal_service_id: null,
        provider_internal_service_id: null
      }
    }
  });

  const { error, setError } = useError();
  const onClose = resetUserSelection;

  useEffect(() => {
    const lastPage = internalServicesData?.at(-1);

    if (
      lastPage?.meta.next_page != null &&
      internalServicesData &&
      internalServicesData.length === internalServicesPages
    ) {
      setInternalServicesPages(internalServicesPages + 1);
    }
  }, [internalServicesData, internalServicesPages, setInternalServicesPages]);

  useEffect(() => {
    if (internalServicesError) {
      setError(internalServicesError);
    }
  }, [internalServicesError]);

  const internalServiceOptions =
    internalServicesData?.flatMap(({ data }) =>
      data.internal_services.map(({ id, name }) => ({ value: id, label: name }))
    ) || [];
  const callerInternalServiceId = watch('service_user_info.caller_internal_service_id');
  const providerInternalServiceId = watch('service_user_info.provider_internal_service_id');

  const onSubmit = async (data: NewUserFormData) => {
    const callerInternalServiceName = internalServiceOptions.find(
      ({ value }) => value === data.service_user_info.caller_internal_service_id
    )?.label;
    const providerInternalServiceName = internalServiceOptions.find(
      ({ value }) => value === data.service_user_info.provider_internal_service_id
    )?.label;

    let name = data.name;
    let email = data.email;

    if (isS2sUser) {
      if (!callerInternalServiceName || !providerInternalServiceName) {
        setError(DEFAULT_ERROR);
        return;
      }

      name ||= `${callerInternalServiceName} to ${providerInternalServiceName}`;
      email ||= `${normalizeInternalServiceNameForEmail(
        callerInternalServiceName
      )}_to_${normalizeInternalServiceNameForEmail(
        providerInternalServiceName
      )}@serviceuser.jobandtalent.com`;
    }

    registerNewUser({ user: { 
      name,
      email,
      time_zone: data.time_zone, 
      locale: data.locale,
      service_user: serviceUserFlag, 
      service_user_info: {
        owner: data.service_user_info.owner,
        description: data.service_user_info.description,
        account_type: accountType,
        var_path: data.service_user_info.var_path,
        ...(isS2sUser && {
          caller_internal_service_id: data.service_user_info.caller_internal_service_id,
          provider_internal_service_id: data.service_user_info.provider_internal_service_id
        })
      } }}).then(
      (response: RegisterNewUser200) => {
        updateQuery(response.data.user.name);
        resetUserSelection();
      },
      (error) => {
        setError(error?.response?.data ? error?.response?.data : DEFAULT_ERROR);
      }
    );
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceUserFlag(event.target.checked);
    const required = event.target.checked ? true : false;
    register('service_user_info.owner', { required });
    register('service_user_info.description', { required });
    register('service_user_info.var_path', { required });
  };
  const handleSelectChange = (newValue: { value: string | number } | null) => {
    setAccountType(newValue?.value as RegisterNewUserBodyUserServiceUserInfoAccountType);
  };
  const ServiceUsersPermissions = !permissionError && permissions.serviceUsersPermission;
  const StackViewFooter = <SaveCancelFooter onClose={onClose} onSelect={handleSubmit(onSubmit)} />;

  return (
    <StackView footer={StackViewFooter} onClose={onClose} title="New user">
      <FormInput label="User name">
        <InputText error={!!errors.name} {...register('name', { required: !isS2sUser })} />
        {!!errors.name && <ErrorMessage>{errors.name.type}</ErrorMessage>}
      </FormInput>
      <FormInput label="User email">
        <InputText error={!!errors.email} {...register('email', { required: !isS2sUser })} />
        {!!errors.email && <ErrorMessage>{errors.email.type}</ErrorMessage>}
      </FormInput>
      <FormInput label="User locale">
        <InputText placeholder="Supported currently en-GB or es-ES"  error={!!errors.locale} {...register('locale', { required: false })} />
        {!!errors.locale && <ErrorMessage>{errors.locale.type}</ErrorMessage>}
      </FormInput>
      <FormInput label="User time zone">
        <InputText placeholder="Europe/Madrid, Europe/London, ..." error={!!errors.time_zone} {...register('time_zone', { required: false })} />
        {!!errors.time_zone && <ErrorMessage>{errors.time_zone.type}</ErrorMessage>}
      </FormInput>
      {ServiceUsersPermissions && 
      <Section icon="info" title="Service User info" defaultCollapsed={true}>
        <FormInput label="Service User">
          <Checkbox
            {...register('service_user', { required: false })}
            disabled={false}
            checked={serviceUserFlag}
            onChange={handleCheckboxChange}
            label="Service or Bot User"
            name="Service User"
            id="service_user"
          />
        </FormInput>
        <FormInput label="Service user owner">
            <InputText placeholder="Service user group owner" error={!!errors.service_user_info?.owner} {...register('service_user_info.owner', { required: false })} />
            {!!errors.service_user_info?.owner && <ErrorMessage>{errors.service_user_info.owner.type}</ErrorMessage>}
        </FormInput>
        <FormInput label="Service user description">
            <InputText placeholder="Service user description" error={!!errors.service_user_info?.description} {...register('service_user_info.description', { required: false })} />
            {!!errors.service_user_info?.description && <ErrorMessage>{errors.service_user_info.description.type}</ErrorMessage>}
        </FormInput>
        <FormInput label="Service user var path">
            <InputText placeholder="Service user var path" error={!!errors.service_user_info?.var_path} {...register('service_user_info.var_path', { required: false })} />
            {!!errors.service_user_info?.var_path && <ErrorMessage>{errors.service_user_info.var_path.type}</ErrorMessage>}
        </FormInput>
        <FormInput label="Service user type: bot, service_to_service, lambda_to_service">
            <Select error={!!errors.service_user_info?.account_type} 
              {...register('service_user_info.account_type', { required: false })}
              onChange={handleSelectChange}
              isMulti={false}
              isClearable={false}
              name="account_type"
              id="account_type"
              options={[
                { value: 'bot', label: 'Bot' }, 
                { value: 'service_to_service', label: 'Service to service' }, 
                { value: 'lambda_to_service', label: 'Lambda to service' },
                { value: 'client', label: 'Client' },
                { value: 'saas', label: 'SaaS' }
              ]}
            />
            {!!errors.service_user_info?.account_type && <ErrorMessage>{errors.service_user_info.account_type.type}</ErrorMessage>}
        </FormInput>
        {isS2sUser && (
          <>
            <FormInput label="Caller internal service">
              <Controller
                name="service_user_info.caller_internal_service_id"
                control={control}
                rules={{ required: 'required' }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    error={!!errors.service_user_info?.caller_internal_service_id}
                    onChange={(option) =>
                      onChange(typeof option?.value === 'number' ? option.value : null)
                    }
                    options={internalServiceOptions.filter(
                      ({ value: id }) => id !== providerInternalServiceId
                    )}
                    value={internalServiceOptions.find(({ value: id }) => id === value)}
                    isClearable={true}
                    isMulti={false}
                    name="caller_internal_service_id"
                    id="caller_internal_service_id"
                  />
                )}
              />
              {!!errors.service_user_info?.caller_internal_service_id && (
                <ErrorMessage>
                  {errors.service_user_info.caller_internal_service_id.message}
                </ErrorMessage>
              )}
            </FormInput>
            <FormInput label="Provider internal service">
              <Controller
                name="service_user_info.provider_internal_service_id"
                control={control}
                rules={{ required: 'required' }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    error={!!errors.service_user_info?.provider_internal_service_id}
                    onChange={(option) =>
                      onChange(typeof option?.value === 'number' ? option.value : null)
                    }
                    options={internalServiceOptions.filter(
                      ({ value: id }) => id !== callerInternalServiceId
                    )}
                    value={internalServiceOptions.find(({ value: id }) => id === value)}
                    isClearable={true}
                    isMulti={false}
                    name="provider_internal_service_id"
                    id="provider_internal_service_id"
                  />
                )}
              />
              {!!errors.service_user_info?.provider_internal_service_id && (
                <ErrorMessage>
                  {errors.service_user_info.provider_internal_service_id.message}
                </ErrorMessage>
              )}
            </FormInput>
          </>
        )}
      </Section>
      }
      {error}
    </StackView>
  );
};

export default NewUserForm;
